import { useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';

import { getSalesLandingInfo } from '@/app/_global/_utils/salesLandingInfo.util';
import { APPLY_ROUTES } from '@/app/_global/_data/routes.constant';

/**
 * Toast 케이스에서 메시지를 읽을 시간을 두고 외부 origin으로 복귀하기까지의 지연.
 * 너무 짧으면 사용자가 toast를 못 읽고, 너무 길면 이탈감이 커진다.
 */
const TOAST_EXIT_DELAY_MS = 3000;

interface UseExitToOriginReturn {
  /** 즉시 외부 origin으로 복귀. modal close 등 동기 액션에서 사용. */
  exitToOrigin: () => void;
  /** TOAST_EXIT_DELAY_MS 후 복귀. toast 메시지를 사용자가 읽을 시간을 둔다. */
  scheduleExitAfterToast: () => void;
}

/**
 * 진행 불가/그만두기/로그인 abort 시점에 외부 sales landing으로 되돌린다.
 * 복귀 우선순위 — ① sessionStorage `salesLandingInfo.url` ② document.referrer(외부 origin만,
 * 같은 origin이면 무한루프 위험이라 제외) ③ /journey/status.
 *
 * 외부로 떠나도 슬롯을 비우지 않는다 — sessionStorage라 back 복귀 시 진입 정보가 살아 있어야
 * 헤더 로고가 유지되고, 탭 종료로 자동 소멸하니 cross-user 누수도 없다. unmount 시 timer를
 * 정리해 다른 화면으로 이동한 뒤 늦게 발화하는 것을 막는다.
 */
export function useExitToOrigin(): UseExitToOriginReturn {
  const router = useRouter();
  const exitTimerRef = useRef<number | null>(null);

  const exitToOrigin = useCallback(() => {
    if (typeof window === 'undefined') return;
    const exitTo = (href: string) => {
      window.location.href = href;
    };
    const storedUrl = getSalesLandingInfo()?.url;
    if (storedUrl) return exitTo(storedUrl);
    const referrer = document.referrer;
    if (referrer && !referrer.startsWith(window.location.origin)) return exitTo(referrer);
    router.replace(APPLY_ROUTES.JOURNEY_STATUS);
  }, [router]);

  const scheduleExitAfterToast = useCallback(() => {
    // 짧은 시간에 두 번 호출되면 직전 타이머가 좀비로 남아, 사용자가 이미 다른 화면에 간 뒤 늦게
    // 발화해 강제 외부 이동 사고가 난다. 새 예약 직전에 정리해 살아있는 타이머를 최대 1개로 고정.
    if (exitTimerRef.current != null) {
      window.clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }
    exitTimerRef.current = window.setTimeout(exitToOrigin, TOAST_EXIT_DELAY_MS);
  }, [exitToOrigin]);

  useEffect(function cleanupExitTimer() {
    return () => {
      if (exitTimerRef.current != null) {
        window.clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
    };
  }, []);

  return { exitToOrigin, scheduleExitAfterToast };
}
